import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { Stack } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import compInfos from "./add-new-trunk-form.json";
import { useLangContext } from "../../context/langContext";
import { number, object, string } from "yup";
import MuiTextField from "../../components/common/text-field/MuiTextField";

import { useProtectedAxiosInstance } from "../../hooks/axiosInstance";
import errorResolver from "../../utils/error-resolver";
import { useNotificationContext } from "../../context/notificationContext";

import { PbxTrunkCreateRequest } from "../../services/Endpoints/pbxTrunk/PbxTrunkCreateEndpoint";
import { PbxTrunkUpdateRequest } from "../../services/Endpoints/pbxTrunk/PbxTrunkUpdateEndpoint";
import { PbxTrunkSchema } from "../../services/Schemas/PbxTrunk";
import MuiSelect from "../../components/common/select/MuiSelect";
import { PbxServersListRequest } from "../../services/Endpoints/pbxserver/PbxServerListEndpoint";
import { AccessEnums } from "../../constants/accessEnum";
import { useCanAccess } from "../../hooks/canAccess";

const successMsg = {
  fa: "ترانک ایجاد/آپدیت شد.",
  en: "New contact has been added.",
};

export interface IAddNewTrunkFormProps {
  onClose: () => void;
  handleReload?: () => void;
  serverInfo?: PbxTrunkSchema;
}
const trunkStatus = {
  active: "فعال",
  inactive: "غیرفعال",
};

export default function AddNewTrunkForm({
  onClose,
  serverInfo,
  handleReload,
}: Readonly<IAddNewTrunkFormProps>) {
  const [waiting, setWaiting] = useState(false);
  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();
  const [data, setData] = useState<{ label: string; value: string }[] | []>([]);

  const getData = async () => {
    try {
      const broadcastingListRequest = new PbxServersListRequest(
        protectedInstance
      );
      setWaiting(true);
      const resp = await broadcastingListRequest.send();
      const broadcast = resp.data.pbx_servers.map((server) => ({
        value: server.id.toString(),
        label: server.caption ?? server.username,
      }));
      setWaiting(false);
      setData(broadcast);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  useEffect(() => {
    getData();
  }, [handleReload]);

  const { lang } = useLangContext();

  const infos = compInfos[lang];
  const loginSchema = object({
    workspace_id: string().nullable(),
    caption: string().required(),
    description: string().required(),
    pbx_trunk_reference: string().required(),
    pbx_server_id: number().required(),
    status: string().required(),
  });

  const formik = useFormik({
    initialValues: {
      pbx_server_id: serverInfo?.pbx_server_id ?? 1,
      pbx_trunk_reference: serverInfo?.pbx_trunk_reference ?? 0,
      workspace_id: null,
      caption: serverInfo?.caption ?? "",
      description: serverInfo?.description ?? "",
      status: "active",
    },
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      setWaiting(true);
      const serverCreateRequest = new PbxTrunkCreateRequest(protectedInstance);
      const serverUpdateRequest = new PbxTrunkUpdateRequest(protectedInstance);
      try {
        const {
          pbx_trunk_reference,
          pbx_server_id,
          workspace_id,
          caption,
          description,
          status,
        } = values;

        const resp = !serverInfo
          ? await serverCreateRequest.send({
              workspace_id: workspace_id,
              pbx_server_id,
              pbx_trunk_reference: String(pbx_trunk_reference),
              caption,
              description,
              status,
            })
          : await serverUpdateRequest.send(serverInfo.id, {
              workspace_id: workspace_id,
              pbx_server_id,
              pbx_trunk_reference: String(pbx_trunk_reference),
              caption,
              description,
              status,
            });
        setWaiting(false);
        handleReload && handleReload();
        onClose();
        setNotification({ type: "success", message: successMsg[lang] });
      } catch (error: any) {
        errorResolver({ error, setNotification });
        setWaiting(false);
      }
    },
    enableReinitialize: true,
  });

  const { can } = useCanAccess();

  return (
    <Stack
      maxWidth="100%"
      width="100%"
      gap="20.31px"
      mx="auto"
      component="form"
      onSubmit={formik.handleSubmit}
    >
      {infos.inputs.map(({ Example, label, name, placeholder }) => (
        <>
          {name == "pbx_server_id" ? (
            <MuiSelect
              label={label}
              name={name}
              placeholder={placeholder}
              value={formik.values[name as "description"]}
              handleChange={formik.handleChange}
              options={
                data.length > 0 ? data : [{ value: "server", label: "1" }]
              }
            />
          ) : (
            <>
              {name == "status" ? (
                <MuiSelect
                  label={label}
                  name={name}
                  placeholder={placeholder}
                  value={formik.values[name as "status"]}
                  handleChange={formik.handleChange}
                  options={Object.keys(trunkStatus).map((key) => ({
                    value: key,
                    label: trunkStatus[key as "active"],
                  }))}
                />
              ) : (
                <MuiTextField
                  key={name}
                  width="100%"
                  label={label}
                  placeholder={placeholder}
                  name={name}
                  inputMode={name == "phone" ? "numeric" : "text"}
                  type="text"
                  value={formik.values[name as "description"]}
                  handleChange={formik.handleChange}
                  multiline={name == "description"}
                  Example={Example}
                  error={
                    formik.touched[name as "description"] &&
                    Boolean(formik.errors[name as "description"]) &&
                    formik.errors[name as "description"]
                  }
                />
              )}
            </>
          )}
        </>
      ))}
      <LoadingButton
        variant="confirm"
        sx={{
          mt: "10px",
        }}
        type="submit"
        loading={waiting}
        disabled={!can(AccessEnums.AdminPbxTrunksCreate)}
      >
        {infos.buttonText}
      </LoadingButton>
    </Stack>
  );
}
